// How a rendered form sends, when the browser cannot do it on its own.
//
// The destination declares its `submission` and the form carries it as a data attribute. `native`
// is not handled here at all — the form posts itself, which is why it keeps working with JavaScript
// off. The two below do not have that property, and the form's own notice says so.
//
//   mailto — the answers become a mail the RESPONDENT sends from their own application. We are not
//            a relay: they see the message before it leaves, which is what makes this destination
//            safe to offer to an anonymous stranger.
//   json   — the answers become a JSON body, which is what most webhook receivers expect and what a
//            browser form never sends by itself.
//   local  — the answers go nowhere and are WRITTEN DOWN in this browser, so the same form can be
//            reopened next month with what was typed in it. The one destination whose receiver is
//            the person filling the form in.
//
// `local` is handled here rather than in a file of its own because it is a way of sending, and this
// is where the ways of sending live — and because it needs the same `answers(form)` the other three
// need. A second reader of the same form, kept in step by hand, is how two of them start disagreeing
// about what a label is.

import { keep } from '../mydata/write-model.js';
import { csrfHeaders } from '@hyperio/core/csrf.js';

const MAILTO_LIMIT = 1800;   // mail clients start truncating well before the 2000-character mark

/** The answers, as [visible question, value] pairs, skipping the backend's own hidden fields. */
function answers(form) {
	const pairs = [];
	new FormData(form).forEach((value, name) => {
		if (name.startsWith('_') || typeof value !== 'string' || !value.trim()) return;
		pairs.push([labelFor(form, name) || name, value]);
	});
	return pairs;
}

// The visible question, not the wire name: a mail reading "full-name: Ada" is worse than one
// reading "Nom complet: Ada", and the label is already on the page in the reader's own language.
function labelFor(form, name) {
	const field = form.querySelector(`[name="${CSS.escape(name)}"]`);
	if (!field) return null;
	const labelled = field.id ? form.querySelector(`label[for="${CSS.escape(field.id)}"]`) : null;
	const own = labelled || field.closest('label');
	return own ? own.textContent.trim() : null;
}

function sendByMail(form) {
	const heading = form.querySelector('.tinyform-title');
	const body = answers(form).map(([label, value]) => `${label}\n${value}`).join('\n\n');
	const address = form.getAttribute('action').replace(/^mailto:/, '');
	window.location.href = 'mailto:' + address
		+ '?subject=' + encodeURIComponent(heading ? heading.textContent.trim() : '')
		+ '&body=' + encodeURIComponent(body.slice(0, MAILTO_LIMIT));
}

/* The same answers the native post would carry, sent to the destination's scripted address. Form
   data rather than JSON: it is what the fallback sends, so both paths behave alike — and it is the
   only shape that can carry a file. */
function sendAsForm(form, url) {
	return fetch(url, { method: 'POST', body: new FormData(form),
		headers: csrfHeaders(url, { Accept: 'application/json' }) });
}

function sendAsJson(form) {
	const body = {};
	new FormData(form).forEach((value, name) => {
		if (!name.startsWith('_')) body[name] = value;
	});
	const url = form.getAttribute('action');
	return fetch(url, {
		method: 'POST',
		headers: csrfHeaders(url, { 'Content-Type': 'application/json' }),
		body: JSON.stringify(body),
	});
}

/* Three states, and the button carries them: someone who pressed send must be able to tell, without
   reading anything, that it is under way — and then that it landed. A form that simply goes quiet is
   pressed twice. */
function mark(form, state) {
	const button = form.querySelector('button[type="submit"]');
	if (button) {
		button.classList.toggle('is-sending', state === 'sending');
		button.classList.toggle('is-sent', state === 'sent');
		button.classList.toggle('is-failed', state === 'failed');
		button.disabled = state === 'sending' || state === 'sent';
	}
	form.classList.toggle('is-sent', state === 'sent');
}

/* `none` sends nowhere, which is not the same as doing nothing: the answers come back on the page.
   That IS the destination's promise — a form whose answers return to the person who filled it —
   and until now it was the one destination that promised something and delivered a dead button. */
function handBack(form) {
	const outcome = form.querySelector('.tinyform-outcome');
	if (!outcome) return;
	outcome.textContent = '';
	const list = document.createElement('dl');
	list.className = 'tinyform-answers';
	answers(form).forEach(([label, value]) => {
		const term = document.createElement('dt');
		term.textContent = label;
		const detail = document.createElement('dd');
		// textContent throughout: these are someone's own words coming back to them, and building
		// this with innerHTML would make a form that scripts whoever fills it.
		detail.textContent = value;
		list.append(term, detail);
	});
	outcome.append(list);
	outcome.hidden = false;
	mark(form, 'sent');
}

/* The wire name beside the visible question. `answers()` drops the name because a mail body has no
   use for it; an entry does — it is what lets a kept answer be put BACK into the field that asked
   for it, and a label is the reader's language rather than the form's contract. */
function namedAnswers(form) {
	const pairs = [];
	new FormData(form).forEach((value, name) => {
		if (name.startsWith('_') || typeof value !== 'string' || !value.trim()) return;
		pairs.push({ name, label: labelFor(form, name) || name, value });
	});
	return pairs;
}

/*
 * `local` writes the answers down and hands them back.
 *
 * Both, and in that order: the hand-back is what `none` already promises and the reason someone
 * pressed the button, and the keeping is the extra this destination exists for. If the store refuses
 * — private browsing, a full quota — the answers are STILL shown, and the notice says they were not
 * kept. Showing nothing because the store failed would lose the one copy that exists.
 */
function keepAnswers(form) {
	const heading = form.querySelector('.tinyform-title');
	const outcome = form.querySelector('.tinyform-outcome');
	return keep({
		kind: 'form',
		title: heading ? heading.textContent.trim() : '',
		// From the markup, where the server put it. Slicing it out of the path looked like the
		// tidier answer — the errand IS the second segment of `/forms/{errand}/{destination}` — and
		// it was wrong under a context path: `/arcate/forms/contact/local` filed every kept entry
		// under "forms". The server knows the errand; it says so rather than leaving it to be
		// reconstructed from an address whose prefix it does not control.
		errand: form.dataset.errand || '',
		// The whole address, so the entry can be reopened exactly as it was served — the token that
		// shapes the form is part of the path, and an entry that reopened a DIFFERENT form would put
		// answers back into the wrong questions.
		source: window.location.pathname + window.location.search,
		origin: window.location.origin,
		answers: namedAnswers(form),
	}).then((done) => {
		handBack(form);
		if (!outcome) return;
		const note = document.createElement('p');
		note.className = 'tinyform-kept' + (done.ok ? '' : ' is-failed');
		note.textContent = done.ok ? (outcome.dataset.kept || '') : (outcome.dataset.keptFailed || '');
		// The way back to everything else that was kept. Only offered when there IS something kept:
		// a link to an empty screen is a promise the screen cannot honour.
		if (done.ok && outcome.dataset.browse && outcome.dataset.browseHref) {
			const link = document.createElement('a');
			link.className = 'tinyform-kept-link';
			link.href = outcome.dataset.browseHref;
			link.textContent = outcome.dataset.browse;
			note.append(' ', link);
		}
		outcome.append(note);
	});
}

function report(form, ok) {
	mark(form, ok ? 'sent' : 'failed');
	const outcome = form.querySelector('.tinyform-outcome');
	if (!outcome) return;
	outcome.textContent = ok ? outcome.dataset.sent : outcome.dataset.failed;
	outcome.hidden = false;
	outcome.classList.toggle('is-failed', !ok);
}

document.querySelectorAll('form.tinyform[data-submission]').forEach((form) => {
	const mode = form.dataset.submission;
	const ajax = form.dataset.ajax;
	// `native` is upgraded only when the destination offers a second, readable address. Without one
	// it is left completely alone — that is what keeps it working with JavaScript off.
	if (mode !== 'mailto' && mode !== 'json' && mode !== 'none' && mode !== 'local' && !ajax) return;

	form.addEventListener('submit', (event) => {
		event.preventDefault();
		if (mode === 'none') {
			handBack(form);
			return;
		}
		if (mode === 'local') {
			keepAnswers(form);
			return;
		}
		if (mode === 'mailto') {
			sendByMail(form);
			mark(form, 'sent');
			return;
		}
		mark(form, 'sending');
		if (ajax) {
			sendAsForm(form, ajax)
				.then((response) => report(form, response.ok))
				.catch(() => report(form, false));
			return;
		}
		// A receiver that sends no CORS header leaves us with an opaque response: the post may well
		// have arrived and we cannot tell. Saying "sent" then would be a guess dressed as a fact, so
		// an answer we cannot read is reported as a failure rather than as a success.
		sendAsJson(form)
			.then((response) => report(form, response.ok))
			.catch(() => report(form, false));
	});
});
